import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import { 
  Users, 
  DollarSign, 
  CreditCard, 
  FileText, 
  ArrowRightLeft,
  FileDown,
  FileSpreadsheet,
  Search
} from "lucide-react";
import ResourcePage from "../../components/ResourcePage";
import { api } from "../../axios/api";
import { notify } from "../../utils/toastAlert";
import { downloadResponse } from "../../utils/downloadFile";

const ContaCorrente = () => {
  const { user, empresa } = useSelector((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [selectedClienteId, setSelectedClienteId] = useState("");
  const [contaData, setContaData] = useState(null);

  // Carregar lista de clientes da empresa para o seletor
  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const response = await api.get("/clientes", {
          params: { empresa_id: empresa?.id || user?.empresa_id }
        });
        const items = response.data?.data || response.data || [];
        const companyId = empresa?.id || user?.empresa_id;
        setClientes(items.filter((cliente) => Number(cliente.empresa_id) === Number(companyId) && !cliente.deletedAt));
      } catch (err) {
        console.error("Erro ao carregar clientes:", err);
      }
    };

    if (empresa?.id || user?.empresa_id) {
      fetchClientes();
    }
  }, [empresa?.id, user?.empresa_id]);

  const fetchContaCorrente = async (cId) => {
    if (!cId) return;
    try {
      setLoading(true);
      const response = await api.post("/caixa/conta-corrente", {
        empresa_id: empresa?.id || user?.empresa_id,
        cliente_id: cId
      });
      setContaData(response.data?.data || response.data);
    } catch (err) {
      console.error("Erro ao carregar conta corrente:", err);
      notify("Erro ao carregar extrato da conta corrente.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClienteChange = (option) => {
    setSelectedClienteId(option?.value || "");
    setContaData(null);
  };

  const clienteOptions = clientes.map((cliente) => ({ value: String(cliente.id), label: `${cliente.nome}${cliente.nif ? ` (NIF: ${cliente.nif})` : ""}` }));
  const selectedCliente = clienteOptions.find((option) => option.value === String(selectedClienteId)) || null;

  // eslint-disable-next-line no-unused-vars
  const handleExportPDF = async () => {
    if (!selectedClienteId) {
      notify("Selecione um cliente primeiro para exportar o extrato.", "warning");
      return;
    }
    try {
      await notify("A gerar extrato em PDF...", "info");
      // Adicione aqui a integração real com o endpoint de PDF se necessário
    } catch (err) {
      await notify("Erro ao exportar PDF.", "error");
    }
  };

  const exportExtrato = async (format) => {
    if (!selectedClienteId) {
      notify("Selecione um cliente primeiro para exportar o extrato.", "warning");
      return;
    }

    try {
      const response = await api.get(`/caixa/conta-corrente/export/${format}`, {
        params: {
          empresa_id: empresa?.id || user?.empresa_id,
          cliente_id: selectedClienteId,
        },
        responseType: "blob",
      });
      downloadResponse(response, `extrato-cliente-${selectedClienteId}.${format === "excel" ? "xls" : "pdf"}`);
      await notify("Extrato exportado com sucesso.", "success");
    } catch (err) {
      await notify(err.message || "Erro ao exportar extrato.", "error");
    }
  };

  const resumo = contaData || { total_faturado: 0, total_liquidado: 0, saldo_atual: 0, movimentos: [] };
  const movimentos = resumo.movimentos || [];

  return (
    <ResourcePage 
      title="Conta Corrente de Clientes" 
      breadcrumb={["Gestão Financeira", "Conta Corrente"]}
      actions={contaData ? (
        <div className="d-flex align-items-center gap-2">
          <button 
            type="button" 
            className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1 shadow-sm"
            onClick={() => exportExtrato("pdf")}
          >
            <FileDown size={16} /> PDF
          </button>
          <button 
            type="button" 
            className="btn btn-outline-success btn-sm d-inline-flex align-items-center gap-1 shadow-sm"
            onClick={() => exportExtrato("excel")}
          >
            <FileSpreadsheet size={16} /> Excel
          </button>
        </div>
      ) : null}
    >
      {/* Seletor de Cliente */}
      <div className="card bg-white border-0 shadow-sm mb-4">
        <div className="card-body py-3">
          <div className="row align-items-center g-3">
            <div className="col-md-auto">
              <span className="fw-semibold text-muted d-flex align-items-center gap-1">
                <Users size={18} /> Selecionar Cliente:
              </span>
            </div>
            <div className="col-md-6">
              <Select value={selectedCliente} options={clienteOptions} onChange={handleClienteChange} placeholder="Pesquisar cliente por nome ou NIF..." isClearable classNamePrefix="app-select" noOptionsMessage={() => "Nenhum cliente desta empresa encontrado"} />
            </div>
            <div className="col-md-auto">
              <button className="btn btn-primary d-inline-flex align-items-center gap-1" type="button" disabled={!selectedClienteId || loading} onClick={() => fetchContaCorrente(selectedClienteId)}>
                <Search size={16} /> {loading ? "A carregar..." : "Carregar dados"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {contaData ? (
        <>
          {/* Cartões Resumo da Conta Corrente */}
          <div className="row mb-4">
            <div className="col-xl-4 col-md-6 mb-4 mb-xl-0">
              <div className="card bg-white border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="text-primary bg-primary-subtle p-2 rounded d-inline-flex mb-3">
                    <FileText size={22} />
                  </div>
                  <p className="text-muted mb-1 small">Total Faturado (Débitos)</p>
                  <h4 className="mb-0 text-primary fw-bold">{Number(resumo.total_faturado).toLocaleString()} Kz</h4>
                </div>
              </div>
            </div>

            <div className="col-xl-4 col-md-6 mb-4 mb-xl-0">
              <div className="card bg-white border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="text-success bg-success-subtle p-2 rounded d-inline-flex mb-3">
                    <CreditCard size={22} />
                  </div>
                  <p className="text-muted mb-1 small">Total Liquidado (Créditos)</p>
                  <h4 className="mb-0 text-success fw-bold">{Number(resumo.total_liquidado).toLocaleString()} Kz</h4>
                </div>
              </div>
            </div>

            <div className="col-xl-4 col-md-12">
              <div className="card bg-white border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className={`p-2 rounded d-inline-flex mb-3 ${resumo.saldo_atual > 0 ? 'text-danger bg-danger-subtle' : 'text-success bg-success-subtle'}`}>
                    <DollarSign size={22} />
                  </div>
                  <p className="text-muted mb-1 small">Saldo Atual em Dívida</p>
                  <h4 className={`mb-0 fw-bold ${resumo.saldo_atual > 0 ? 'text-danger' : 'text-success'}`}>
                    {Number(resumo.saldo_atual).toLocaleString()} Kz
                  </h4>
                </div>
              </div>
            </div>
          </div>

          {/* Extrato / Movimentos da Conta Corrente */}
          <div className="card bg-white border-0 shadow-sm">
            <div className="card-body">
              <h4 className="card-title mb-3 fs-5 d-flex align-items-center gap-2">
                <ArrowRightLeft size={20} className="text-primary" /> Histórico de Movimentos
              </h4>
              <div className="table-responsive">
                <table className="table table-centered table-hover mb-0 align-middle">
                  <thead>
                    <tr className="text-muted fs-7">
                      <th>Tipo / Nº</th>
                      <th>Data</th>
                      <th>Valor</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movimentos.length > 0 ? (
                      movimentos.map((mov) => (
                        <tr key={mov.id}>
                          <td><span className="fw-semibold">{mov.tipo}</span> {mov.numero}</td>
                          <td>{mov.data}</td>
                          <td className="fw-bold">{Number(mov.valor).toLocaleString()} Kz</td>
                          <td>
                            <span className={`badge ${mov.estado === "Validado" ? "bg-success-subtle text-success" : "bg-warning-subtle text-warning"}`}>
                              {mov.estado}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-4">Nenhum movimento registado para este cliente.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="card bg-white border-0 shadow-sm text-center py-5">
          <div className="card-body text-muted">
            <Users size={48} className="mb-3 opacity-50" />
            <h5>Conta corrente não carregada</h5>
            <p className="small mb-0">Selecione um cliente da empresa e clique em “Carregar dados”.</p>
          </div>
        </div>
      )}
    </ResourcePage>
  );
};

export default ContaCorrente;
